import React, {useEffect, useRef, useState} from "react";
import {Contractor, DoopUser} from "../Interfaces/IDOOP";
import {sendUserDelete} from "../BackendFacade/apiCalls";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxTable from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import {odxIconSets} from "../../../OdxComponents/constants";
import OdxIcon from "../../../OdxComponents/NativeOdx/Odx-Icon";
import EditUserModal from "./EditUserModal";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";
import {useDoopData} from "../doopDataContext";

const EditUsers: React.FC = () => {
    const [popup, setPopup] = useState(false);
    const [selectedUser, setSelectedUser] = useState<DoopUser | null>(null);
    const {users, meta, refresh, error} = useDoopData();
    const {setLoading, createErrorToast} = useMain();
    const setLoadingRef = useRef(setLoading);
    const createErrorToastRef = useRef(createErrorToast);

    useEffect(() => {
        setLoadingRef.current = setLoading;
    }, [setLoading]);

    useEffect(() => {
        createErrorToastRef.current = createErrorToast;
    }, [createErrorToast]);

    useModuleViewTracking('doop-edituser')

    const editUserShow = (user?: DoopUser) => {
        if (user) {
            setSelectedUser(user);
        } else {
            setSelectedUser(null);
        }
        setPopup(true);
    };

    const deleteUserConfirm = (email: string) => {
        setLoadingRef.current(true);
        sendUserDelete(email, HubPathRouting.currentProject.id, HubPathRouting.currentModule.id).then(
            data2 => {
                if (!data2.success) {
                    createErrorToastRef.current("Error", data2.message);
                    setLoadingRef.current(false);
                } else {
                    refresh()
                        .catch((error) => {
                            const message = error instanceof Error ? error.message : "Failed to load project data";
                            createErrorToastRef.current("Error", message);
                        })
                        .finally(() => {
                            setLoadingRef.current(false);
                        });
                }
            }
        );
    };

    if (error) {
        return <div>{error}</div>;
    }

    if (meta === null) {
        return <></>;
    }

    return (
        <>
            <OdxBreadcrumbs/>

            {popup && (
                <EditUserModal
                    user={selectedUser}
                    closePopup={() => setPopup(false)}
                    deleteUser={deleteUserConfirm}
                />
            )}
            <div className="p-4">
                <div className="flex justify-between items-center">
                    <OdxAreaHeader
                        text="User Management"
                        odxSize="medium"
                        className={'!pl-0 !pr-0'}
                    />
                    {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id) && (
                        <div className="mt-4">
                            <odx-button
                                id="Doop_Users_AddUserBtn"
                                variant="accent"
                                size="lg"
                                type="button"
                                onClick={() => editUserShow()}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                Add User
                            </odx-button>
                        </div>
                    )}
                </div>

                <div className="mt-4">
                    <OdxTable
                        id={"Doop_Users_Table"}
                        headerData={[
                            {name: 'email', title: 'Email', required: true},
                            {name: 'name', title: 'Name', required: true},
                            {name: 'areas', title: 'Areas', required: true},
                            {name: 'canInitiateProcess', title: 'PO/PM', className: 'text-center', required: true},
                            {
                                name: 'canAcceptOrders',
                                title: 'Service Manager',
                                className: 'text-center',
                                required: true
                            },
                            {name: 'isContractor', title: 'Contractor', className: 'text-center', required: true},
                            {
                                name: 'isOrderInformed',
                                title: 'Ext. Service Manager',
                                className: 'text-center',
                                required: true
                            },
                            isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id) && {
                                name: 'actions',
                                title: 'Actions',
                                className: 'text-center',
                                required: true
                            }
                        ].filter(Boolean)}
                    >
                        {users.map((user, index) => (
                            <OdxTableRow key={index} id={"Doop_Users_Row_" + index}>
                                <OdxTableCell>{user.email}</OdxTableCell>
                                <OdxTableCell>{user.name}</OdxTableCell>
                                <OdxTableCell>{user.areas.length === 0 ? 'All' : user.areas.join(", ")}</OdxTableCell>
                                <OdxTableCell className="text-center">{user.canInitiateProcess ?
                                    <OdxIcon name="check" className={"text-green-500"} iconSet={odxIconSets.core}
                                             size="md"/> :
                                    <OdxIcon name="close" className={"text-red-500"} iconSet={odxIconSets.core}
                                             size="md"/>}</OdxTableCell>
                                <OdxTableCell className="text-center">{user.canAcceptOrders ?
                                    <OdxIcon name="check" className={"text-green-500"} iconSet={odxIconSets.core}
                                             size="md"/> :
                                    <OdxIcon name="close" className={"text-red-500"} iconSet={odxIconSets.core}
                                             size="md"/>}</OdxTableCell>
                                <OdxTableCell className="text-center">{user.isContractor ?
                                    <OdxIcon name="check" className={"text-green-500"} iconSet={odxIconSets.core}
                                             size="md"/> :
                                    <OdxIcon name="close" className={"text-red-500"} iconSet={odxIconSets.core}
                                             size="md"/>}</OdxTableCell>
                                <OdxTableCell className="text-center">{user.isOrderInformed ?
                                    <OdxIcon name="check" className={"text-green-500"} iconSet={odxIconSets.core}
                                             size="md"/> :
                                    <OdxIcon name="close" className={"text-red-500"} iconSet={odxIconSets.core}
                                             size="md"/>}</OdxTableCell>
                                {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id) && (
                                    <OdxTableCell className="flex space-x-2 justify-center">
                                        <odx-icon-button
                                            id={`Doop_Users_Edit_${index}`}
                                            variant="ghost"
                                            size="lg"
                                            icon={`${odxIconSets.core}::chevron-right`}
                                            onClick={() => editUserShow(user)}
                                        />
                                    </OdxTableCell>
                                )}
                            </OdxTableRow>
                        ))}
                    </OdxTable>

                </div>
            </div>
        </>
    );
};

export default EditUsers;
